import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import { userService } from '../../services/userService';
import { User, formatLastLogin } from '../../types/user';
import { useToast } from '../../contexts/ToastContext';

/* -- Status Badge ---------------------------------------------- */
const StatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const config = isActive
    ? { color: 'text-primary', dotColor: 'bg-primary animate-pulse shadow-lg shadow-primary/50', label: 'Active' }
    : { color: 'text-slate-500', dotColor: 'bg-slate-400', label: 'Inactive' };

  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
      <span className={`text-sm font-bold ${config.color}`}>{config.label}</span>
    </div>
  );
};

/* -- Role Badge ------------------------------------------------ */
const RoleBadge: React.FC<{ roleName: string; isSystemRole: boolean }> = ({ roleName, isSystemRole }) => {
  const lower = roleName.toLowerCase();
  let config = { bg: 'bg-slate-500/10', text: 'text-slate-500', border: 'border-slate-500/20' };

  if (lower === 'superadmin') {
    config = { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' };
  } else if (lower === 'admin' || lower === 'owner') {
    config = { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20' };
  } else if (lower === 'support' || lower === 'finance') {
    config = { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20' };
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${config.bg} ${config.text} border ${config.border}`}>
      {isSystemRole ? '★ ' : ''}{roleName}
    </span>
  );
};

/* -- Delete Confirm Dialog ------------------------------------- */
const DeleteConfirm: React.FC<{
  user: User;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}> = ({ user, onConfirm, onCancel, isDeleting }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white border border-slate-200 rounded-2xl p-8 w-full max-w-md text-center">
      <div className="size-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="material-symbols-outlined text-3xl text-red-500">warning</span>
      </div>
      <h3 className="text-xl font-bold mb-2 text-slate-900">Delete User</h3>
      <p className="text-slate-500 text-sm mb-6">
        Are you sure you want to delete <strong className="text-slate-900">{user.fullName}</strong> ({user.email})?
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" disabled={isDeleting}>Cancel</button>
        <button onClick={onConfirm} className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-2" disabled={isDeleting}>
          {isDeleting ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Deleting...</>) : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

/* -- Users Page ------------------------------------------------ */
const UsersPage: React.FC = () => {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await userService.getSystemAdmins();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter((user) => {
    if (activeFilter === 'active' && !user.isActive) return false;
    if (activeFilter === 'inactive' && user.isActive) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        user.fullName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role?.name.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleToggleActive = async (user: User) => {
    try {
      if (user.isActive) {
        await userService.deactivate(user.id);
        toast.success(`${user.fullName} deactivated`);
      } else {
        await userService.activate(user.id);
        toast.success(`${user.fullName} activated`);
      }
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await userService.delete(deleteTarget.id);
      toast.success(`${deleteTarget.fullName} deleted`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const activeCount = users.filter(u => u.isActive).length;

  return (
    <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
      <Header searchPlaceholder="Search users by name, email, or role..." onSearch={setSearchQuery} />

      <div className="flex-1 overflow-y-auto p-8">
        {/* Page Title & Actions */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">User Management</h1>
            <p className="text-slate-500 mt-1">Manage platform users, their roles and access permissions.</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all transform active:scale-95 shadow-lg shadow-primary/20"
          >
            <span className="material-symbols-outlined text-xl">person_add</span>
            Add New User
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">Total System Users</p>
            <span className="text-2xl font-black text-slate-900">{users.length}</span>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">Active</p>
            <span className="text-2xl font-black text-primary">{activeCount}</span>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-xl">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">Inactive</p>
            <span className="text-2xl font-black text-slate-500">{users.length - activeCount}</span>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-500 text-sm font-medium flex items-center gap-3">
            <span className="material-symbols-outlined">error</span>
            {error}
            <button onClick={fetchUsers} className="ml-auto text-red-500 hover:text-red-600 underline text-xs">Retry</button>
          </div>
        )}

        {/* Table Container */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Table Filters */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(['all', 'active', 'inactive'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                    activeFilter === filter
                      ? 'bg-slate-100 text-slate-700 border border-slate-200'
                      : 'text-slate-500 hover:text-slate-600'
                  }`}
                >
                  {filter === 'all' ? 'All Users' : filter === 'active' ? 'Active Only' : 'Inactive'}
                </button>
              ))}
            </div>
          </div>

          {/* Loading / Empty / Table */}
          {isLoading ? (
            <div className="p-16 flex items-center justify-center">
              <div className="flex items-center gap-3 text-slate-500">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Loading users...</span>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-16 flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-4xl text-slate-400 mb-3">group</span>
              <p className="text-slate-500 font-bold">No users found</p>
              <p className="text-slate-400 text-sm mt-1">
                {users.length === 0 ? 'Create your first system user to get started.' : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[11px] font-black uppercase tracking-widest">
                    <th className="px-6 py-4">User</th>
                    <th className="px-6 py-4">Role</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Last Login</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-primary/5 group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 border border-slate-200 flex items-center justify-center text-slate-700 font-bold text-sm">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{user.fullName}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <RoleBadge roleName={user.role?.name || 'Unknown'} isSystemRole={user.role?.isSystemRole || false} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge isActive={user.isActive} />
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${!user.lastLoginAt ? 'text-slate-400 italic' : 'text-slate-500'}`}>
                          {formatLastLogin(user.lastLoginAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditTarget(user)}
                            className="p-2 text-slate-400 hover:text-blue-500 transition-colors"
                            title="Edit"
                          >
                            <span className="material-symbols-outlined text-lg">edit</span>
                          </button>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className={`p-2 transition-colors ${user.isActive ? 'text-slate-400 hover:text-amber-500' : 'text-slate-400 hover:text-emerald-500'}`}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <span className="material-symbols-outlined text-lg">
                              {user.isActive ? 'pause_circle' : 'play_circle'}
                            </span>
                          </button>
                          <button
                            onClick={() => setDeleteTarget(user)}
                            className="p-2 text-slate-400 hover:text-primary transition-colors"
                            title="Delete"
                          >
                            <span className="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {!isLoading && filteredUsers.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 font-medium tracking-wide">
                Showing {filteredUsers.length} of {users.length} system users
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchUsers}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={!!editTarget}
        user={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={fetchUsers}
      />

      {/* Delete Confirmation */}
      {deleteTarget && (
        <DeleteConfirm
          user={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
    </main>
  );
};

export default UsersPage;
